import i18n from 'i18next';
/** 날짜·시간 다루기.
 *
 * 로컬 ISO 변환과 "오전/오후 h:mm" 파싱이 화면마다 복사돼 있었습니다.
 * 같은 규칙이 두 벌 있으면 한쪽만 고쳐져 시간이 12시간 어긋나는 식으로 갈라집니다.
 */

/** 시각을 서버로 보낼 때 — UTC.
 *
 *  서버는 시각을 UTC 로 저장하고 UTC 로 돌려줍니다. 벽시계 문자열을 보내면
 *  어느 나라 시각인지 알 수 없어, 서버가 자기 시간대로 해석해 어긋납니다.
 *  모임 시각·서브 장소 시각·번개 시각처럼 "그 순간"을 뜻하는 값은 전부 이걸 씁니다. */
export const toUtcISOString = (date: Date): string => date.toISOString();

/** `<input type="datetime-local">` 값(로컬 벽시계)을 서버용 UTC 로.
 *  예: "2026-09-08T16:37" → "2026-09-08T07:37:00.000Z" */
export const localInputToUtc = (localValue: string): string => {
    const d = new Date(localValue);
    return Number.isNaN(d.getTime()) ? localValue : d.toISOString();
};

/** 날짜(시각 아님)를 보낼 때 — 로컬 벽시계 그대로.
 *
 *  여행 시작일처럼 "날짜"인 값은 UTC 로 바꾸면 보는 지역에 따라 하루가 밀립니다.
 *  서버도 이 필드들만 오프셋 없는 형식으로 주고받습니다(UtcDateTimeConfig 제외 대상). */
export const toLocalISOString = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
        + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

/**
 * 시각 문자열 → 자정부터의 분. 못 읽으면 null.
 *
 * 앱에 시각 형식이 두 가지 섞여 있습니다.
 *   · "오후 6:00" — TimePicker 와 여행 집결지 칩이 만드는 형식. DB 의 meeting_time 은 전부 이것.
 *   · "18:00"    — input type=time, 프리셋 칩, meeting_sub.visit_time 다수.
 * 어느 쪽이 들어와도 읽어야 해서 같은 파서가 화면마다 따로 복사돼 있었고,
 * TravelGatherPlace 는 파서 대신 문자열 길이로 형식을 판정하다 "오후 6:00"(길이 7)을
 * 못 읽고 자정으로 계산했습니다. 형식을 통일하는 대신 읽는 쪽을 여기 하나로 모읍니다.
 */
export const parseTimeToMinutes = (text?: string | null): number | null => {
    if (!text) return null;
    const hhmm = text.match(/^(\d{1,2}):(\d{2})$/);
    if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);
    /* "오후 6:00" 외에 "오후 6시", "낮 12시" 도 들어옵니다 */
    const ko = text.match(/(오전|오후|낮)\s*(\d{1,2})(?:시|:)?\s*(\d{0,2})?/);
    if (ko) {
        let hour = parseInt(ko[2], 10);
        const minute = ko[3] ? parseInt(ko[3], 10) : 0;
        if (ko[1] === '오후' && hour !== 12) hour += 12;
        else if (ko[1] === '오전' && hour === 12) hour = 0;
        else if (ko[1] === '낮') hour = 12;
        return hour * 60 + minute;
    }
    return null;
};

/**
 * "HH:mm" → "오후 6:00". 못 읽으면 빈 문자열.
 *
 * toHHmm 의 반대입니다. meeting.meeting_time 에 쌓인 값은 전부 이 형식이고
 * TimePicker · 여행 집결지 칩도 이 형식을 만듭니다. input type=time 처럼
 * HH:mm 만 다루는 입력에서 값을 받아 저장할 때 형식을 되돌려 한 컬럼에
 * 두 형식이 섞이지 않게 합니다.
 */
export const toKoreanTime = (hhmm?: string | null): string => {
    const mins = parseTimeToMinutes(hhmm);
    if (mins == null) return '';
    const h24 = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    const period = h24 >= 12 ? '오후' : '오전';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return `${period} ${h12}:${String(m).padStart(2, '0')}`;
};

/** 시각 문자열 → "HH:mm". 못 읽으면 null. (input type=time 처럼 형식이 고정된 곳에) */
export const toHHmm = (text?: string | null): string | null => {
    const mins = parseTimeToMinutes(text);
    if (mins == null) return null;
    const h = Math.floor(mins / 60) % 24;
    const m = mins % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};


/**
 * 지난 시간을 사람 말로 — "방금 전 / 3분 전 / 2시간 전 / 3일 전 / 9/28".
 *
 * 일주일이 넘으면 상대 표현이 오히려 안 잡혀서 날짜로 바꿉니다.
 * 모임 후기와 모임 기록이 같은 기준을 써야 해서 여기 둡니다.
 */
export const formatRelative = (iso?: string | null): string => {
    if (!iso) return '';
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return '';
    const diff = Date.now() - ts;
    if (diff < 60_000) return i18n.t('posts.justNow');
    if (diff < 3600_000) return i18n.t('posts.minAgo', { n: Math.round(diff / 60_000) });
    if (diff < 86400_000) return i18n.t('posts.hourAgo', { n: Math.round(diff / 3600_000) });
    if (diff < 7 * 86400_000) return i18n.t('posts.dayAgo', { n: Math.round(diff / 86400_000) });
    const d = new Date(ts);
    return `${d.getMonth() + 1}/${d.getDate()}`;
};
